import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Eye, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { OrderDetailDialog } from './components/OrderDetailDialog';
import { ShipOrderDialog } from './components/ShipOrderDialog';
import { ProcessOrderDialog } from './components/ProcessOrderDialog';
import { DeleteOrderDialog } from './components/DeleteOrderDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipForm, setShipForm] = useState({ tracking_number: '', carrier: 'Australia Post', estimated_delivery: '3-5 business days' });
  const [orderSerials, setOrderSerials] = useState([]);
  const [availableSerials, setAvailableSerials] = useState({});
  const [productTrackInfo, setProductTrackInfo] = useState({});
  const [shipSerialAllocations, setShipSerialAllocations] = useState({});
  const [shipLoading, setShipLoading] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processSerialAllocations, setProcessSerialAllocations] = useState({});
  const [processLoading, setProcessLoading] = useState(false);

  useEffect(() => { fetchOrders(); }, [pagination.page, statusFilter]);

  // Handle search highlight - auto-open order from global search
  useEffect(() => {
    const highlightId = searchParams.get('highlight') || location.state?.highlightId;
    if (!highlightId) return;

    const openOrder = async (id) => {
      let order = orders.find(o => o.id === id);
      if (!order) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          order = res.data;
        } catch { return; }
      }
      if (order) {
        setSelectedOrder(order);
        setDialogOpen(true);
      }
      setSearchParams({}, { replace: true });
      window.history.replaceState({}, '');
    };

    openOrder(highlightId);
  }, [orders, searchParams, location.state]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', pagination.page.toString());
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API}/admin/orders?${params.toString()}`);
      setOrders(res.data.orders);
      setPagination({ page: res.data.page, pages: res.data.pages, total: res.data.total });
    } catch (err) { console.error('Error fetching orders:', err); }
    finally { setLoading(false); }
  };

  const updateOrder = async (orderId, updates) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, updates);
      toast.success('Order updated');
      fetchOrders();
      // Keep dialog open and refresh selected order with new data
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, ...updates }));
        loadOrderSerials(orderId);
      }
    } catch { toast.error('Failed to update order'); }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      processing: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-[#78BE20]/20 text-[#78BE20]',
      cancelled: 'bg-red-500/20 text-red-500',
      paid: 'bg-[#78BE20]/20 text-[#78BE20]',
      failed: 'bg-red-500/20 text-red-500',
      refunded: 'bg-orange-500/20 text-orange-500',
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-500';
  };

  const getPaymentDisplay = (order) => {
    if (order.status === 'cancelled' && order.payment_status === 'pending') {
      return { status: 'cancelled', label: 'Cancelled' };
    }
    return { status: order.payment_status, label: order.payment_status };
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderToDelete.id}`);
      toast.success('Order deleted permanently');
      setDeleteDialogOpen(false); setOrderToDelete(null); fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete order'); }
  };

  const handleResendConfirmation = async (order) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/resend-confirmation`);
      toast.success('Confirmation email sent to customer');
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to resend confirmation email'); 
    }
  };

  const handleSendInvoice = async (order) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/send-invoice`);
      toast.success('Invoice sent to customer with payment details');
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to send invoice'); 
    }
  };

  const handleMarkOrderPaid = async (order, paymentMethod) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/mark-paid`, {
        payment_method: paymentMethod,
        send_receipt: true
      });
      toast.success(`Order marked as paid via ${paymentMethod}`);
      fetchOrders();
      if (selectedOrder?.id === order.id) {
        setSelectedOrder({ ...selectedOrder, payment_status: 'paid', payment_method: paymentMethod });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark order as paid');
    }
  };

  const loadOrderSerials = async (orderId) => {
    try { const res = await axios.get(`${API}/admin/orders/${orderId}/serials`); setOrderSerials(res.data.serial_numbers || []); }
    catch { setOrderSerials([]); }
  };

  const loadAvailableSerials = async (items) => {
    const available = {};
    const trackInfo = {};
    for (const item of items) {
      try {
        const res = await axios.get(`${API}/admin/products/${item.product_id}/serials?status=in_stock`);
        available[item.product_id] = res.data.serial_numbers || [];
        trackInfo[item.product_id] = res.data.track_serials || false;
      } catch {
        available[item.product_id] = [];
        trackInfo[item.product_id] = false;
      }
    }
    setAvailableSerials(available);
    setProductTrackInfo(trackInfo);
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.shipping?.tracking_number || '');
    setDialogOpen(true);
    loadOrderSerials(order.id);
    loadAvailableSerials(order.items || []);
  };

  const handleOpenShipDialog = () => {
    if (!selectedOrder) return;
    setShipForm({
      tracking_number: selectedOrder.shipping?.tracking_number || trackingNumber || '',
      carrier: 'Australia Post',
      estimated_delivery: '3-5 business days',
    });
    const allocs = {};
    (selectedOrder.items || []).forEach(item => { allocs[item.product_id] = ''; });
    setShipSerialAllocations(allocs);
    setShipDialogOpen(true);
  };

  const handleConfirmShip = async () => {
    setShipLoading(true);
    try {
      const allocations = [];
      for (const [productId, text] of Object.entries(shipSerialAllocations)) {
        const sns = text.split('\n').map(s => s.trim()).filter(Boolean);
        if (sns.length > 0) allocations.push({ product_id: productId, serial_numbers: sns });
      }
      if (allocations.length > 0) {
        await axios.post(`${API}/admin/orders/${selectedOrder.id}/allocate-serials`, { allocations });
      }

      const serialsRes = await axios.get(`${API}/admin/orders/${selectedOrder.id}/serials`);
      const currentSerials = serialsRes.data.serial_numbers || [];
      const trackedItems = (selectedOrder.items || []).filter(item => productTrackInfo[item.product_id]);
      const unallocated = trackedItems.filter(item => {
        const count = currentSerials.filter(s => s.product_id === item.product_id).length;
        return count < item.quantity;
      });

      if (unallocated.length > 0) {
        toast.error(`Missing serial numbers for: ${unallocated.map(i => i.product_name).join(', ')}. Please allocate all serials before shipping.`);
        setShipLoading(false);
        return;
      }

      await updateOrder(selectedOrder.id, {
        status: 'shipped',
        tracking_number: shipForm.tracking_number,
        carrier: shipForm.carrier,
        estimated_delivery: shipForm.estimated_delivery,
      });
      setShipDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to ship order'); }
    finally { setShipLoading(false); }
  };

  const handleProcessingStatusChange = async (order) => {
    let hasTracked = false;
    const freshTrackInfo = {};
    const freshAvailable = {};
    for (const item of (order.items || [])) {
      try {
        const res = await axios.get(`${API}/admin/products/${item.product_id}/serials?status=in_stock`);
        freshTrackInfo[item.product_id] = res.data.track_serials || false;
        freshAvailable[item.product_id] = res.data.serial_numbers || [];
        if (res.data.track_serials) hasTracked = true;
      } catch {
        freshTrackInfo[item.product_id] = false;
        freshAvailable[item.product_id] = [];
      }
    }
    setProductTrackInfo(freshTrackInfo);
    setAvailableSerials(freshAvailable);

    if (!hasTracked) { updateOrder(order.id, { status: 'processing' }); return; }

    const serialsRes = await axios.get(`${API}/admin/orders/${order.id}/serials`);
    const currentSerials = serialsRes.data.serial_numbers || [];
    setOrderSerials(currentSerials);

    const hasUnallocated = (order.items || []).some(item => {
      if (!freshTrackInfo[item.product_id]) return false;
      const allocated = currentSerials.filter(s => s.product_id === item.product_id).length;
      return allocated < item.quantity;
    });

    if (hasUnallocated) {
      const allocs = {};
      (order.items || []).forEach(item => { allocs[item.product_id] = ''; });
      setProcessSerialAllocations(allocs);
      setDialogOpen(false);
      setTimeout(() => setProcessDialogOpen(true), 200);
    } else {
      updateOrder(order.id, { status: 'processing' });
    }
  };

  const handleConfirmProcess = async () => {
    setProcessLoading(true);
    try {
      const allocations = [];
      for (const [productId, text] of Object.entries(processSerialAllocations)) {
        const sns = text.split('\n').map(s => s.trim()).filter(Boolean);
        if (sns.length > 0) allocations.push({ product_id: productId, serial_numbers: sns });
      }
      if (allocations.length > 0) {
        await axios.post(`${API}/admin/orders/${selectedOrder.id}/allocate-serials`, { allocations });
      }

      const serialsRes = await axios.get(`${API}/admin/orders/${selectedOrder.id}/serials`);
      const currentSerials = serialsRes.data.serial_numbers || [];
      const trackedItems = (selectedOrder.items || []).filter(item => productTrackInfo[item.product_id]);
      const unallocated = trackedItems.filter(item => {
        const count = currentSerials.filter(s => s.product_id === item.product_id).length;
        return count < item.quantity;
      });

      if (unallocated.length > 0) {
        toast.error(`Missing serial numbers for: ${unallocated.map(i => i.product_name).join(', ')}`);
        setProcessLoading(false);
        return;
      }

      await updateOrder(selectedOrder.id, { status: 'processing' });
      setProcessDialogOpen(false);
      // Reopen order detail dialog so user can continue (e.g. shipping)
      setDialogOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to process order'); }
    finally { setProcessLoading(false); }
  };

  const handleDeallocateSerial = async (serialNumber) => {
    try {
      await axios.delete(`${API}/admin/orders/${selectedOrder.id}/deallocate-serial/${encodeURIComponent(serialNumber)}`);
      toast.success('Serial removed');
      loadOrderSerials(selectedOrder.id);
      loadAvailableSerials(selectedOrder.items || []);
    } catch { toast.error('Failed to remove serial'); }
  };

  const handleSyncXero = async (order) => {
    try {
      const res = await axios.post(`${API}/xero/sync-order/${order.id}`);
      toast.success(`Synced to Xero: Invoice ${res.data.invoice_number}`);
      fetchOrders();
      setDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to sync to Xero'); }
  };

  const handleOpenDeleteFromDetail = (order) => {
    setDialogOpen(false);
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">Orders</h1>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={7} className="px-4 py-4"><div className="h-8 bg-zinc-800 rounded animate-pulse" /></td></tr>
                ))
              ) : orders.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No orders found</td></tr>
              ) : (
                orders.map(order => {
                  const paymentDisplay = getPaymentDisplay(order);
                  return (
                    <tr key={order.id} className="hover:bg-zinc-900/50">
                      <td className="px-4 py-4">
                        <p className="text-white font-mono">#{order.order_number || order.id.slice(0, 8).toUpperCase()}</p>
                        {order.xero_invoice_id && <p className="text-xs text-zinc-500">Xero: {order.xero_invoice_id.slice(0, 8)}...</p>}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-white">{order.user?.first_name} {order.user?.last_name}</p>
                        <p className="text-xs text-zinc-500">{order.user?.email}</p>
                      </td>
                      <td className="px-4 py-4"><Badge className={getStatusColor(order.status)}>{order.status}</Badge></td>
                      <td className="px-4 py-4"><Badge className={getStatusColor(paymentDisplay.status)}>{paymentDisplay.label}</Badge></td>
                      <td className="px-4 py-4 text-[#78BE20] font-mono font-bold">${order.total.toFixed(2)}</td>
                      <td className="px-4 py-4 text-zinc-400 text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-4 text-right space-x-1">
                        <Button variant="ghost" size="icon" onClick={() => openOrderDetail(order)} className="text-zinc-400 hover:text-white"><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => { setOrderToDelete(order); setDeleteDialogOpen(true); }} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" size="icon" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} className="border-zinc-700"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-zinc-400 px-4">Page {pagination.page} of {pagination.pages}</span>
            <Button variant="outline" size="icon" disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} className="border-zinc-700"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}

        {/* Dialogs */}
        <OrderDetailDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          order={selectedOrder}
          orderSerials={orderSerials}
          productTrackInfo={productTrackInfo}
          trackingNumber={trackingNumber}
          setTrackingNumber={setTrackingNumber}
          onUpdateOrder={updateOrder}
          onDeallocateSerial={handleDeallocateSerial}
          onShip={handleOpenShipDialog}
          onProcess={handleProcessingStatusChange}
          onDelete={handleOpenDeleteFromDetail}
          onSyncXero={handleSyncXero}
          onResendConfirmation={handleResendConfirmation}
          onSendInvoice={handleSendInvoice}
          onMarkPaid={handleMarkOrderPaid}
          getStatusColor={getStatusColor}
        />

        <ShipOrderDialog
          open={shipDialogOpen}
          onOpenChange={setShipDialogOpen}
          order={selectedOrder}
          shipForm={shipForm}
          setShipForm={setShipForm}
          orderSerials={orderSerials}
          productTrackInfo={productTrackInfo}
          availableSerials={availableSerials}
          shipSerialAllocations={shipSerialAllocations}
          setShipSerialAllocations={setShipSerialAllocations}
          shipLoading={shipLoading}
          onConfirmShip={handleConfirmShip}
        />

        <ProcessOrderDialog
          open={processDialogOpen}
          onOpenChange={setProcessDialogOpen}
          order={selectedOrder}
          orderSerials={orderSerials}
          productTrackInfo={productTrackInfo}
          availableSerials={availableSerials}
          processSerialAllocations={processSerialAllocations}
          setProcessSerialAllocations={setProcessSerialAllocations}
          processLoading={processLoading}
          onConfirmProcess={handleConfirmProcess}
        />

        <DeleteOrderDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          order={orderToDelete}
          onConfirmDelete={handleDeleteOrder}
        />
      </div>
    </AdminLayout>
  );
}
